import { addDays, max as latestOf, min as earliestOf } from 'date-fns';

// Parse a 'YYYY-MM-DD' (or ISO) date-only string at local noon so the calendar day can't shift
// across timezones. Pass-through for Date instances; undefined for empty/invalid input.
const parseDateOnlyAsLocalNoon = (value) => {
  if (!value) {
    return undefined;
  }
  if (value instanceof Date) {
    return value;
  }
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) {
    return undefined;
  }
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

/**
 * Selectable effective-date bounds for the "Dwelling under construction (theft)" coverage (#32149).
 *
 * - Agent: from 5 days before today to 60 days after today, but never outside the policy term
 *   (earliest is clamped up to the policy effective date, latest clamped down to expiration).
 * - Underwriter: the full policy term (effective..expiration), no relative window.
 *
 * `today` is injected for deterministic tests. Returns `{ minDate, maxDate }` as Date objects;
 * a bound is `undefined` when unconstrained (e.g. UW before a policy term exists), which the
 * DatePicker treats as its wide default.
 *
 * @param {Object} params
 * @param {boolean} params.isUnderwriter - Whether the current user can approve UW review.
 * @param {Date} [params.today] - "Today" reference (defaults to now).
 * @param {string|Date} [params.policyEffectiveDate] - Policy effective date (YYYY-MM-DD).
 * @param {string|Date} [params.policyExpirationDate] - Policy expiration date (YYYY-MM-DD).
 * @returns {{ minDate: Date|undefined, maxDate: Date|undefined }}
 */
export const getUnderConstructionTheftDateBounds = ({
  isUnderwriter,
  today,
  policyEffectiveDate,
  policyExpirationDate
}) => {
  const effective = parseDateOnlyAsLocalNoon(policyEffectiveDate);
  const expiration = parseDateOnlyAsLocalNoon(policyExpirationDate);

  // Underwriters may pick any date within the policy term.
  if (isUnderwriter) {
    return { minDate: effective, maxDate: expiration };
  }

  const now = today instanceof Date ? today : new Date();
  const agentEarliest = addDays(now, -5);
  const agentLatest = addDays(now, 60);

  return {
    minDate: effective ? latestOf([agentEarliest, effective]) : agentEarliest,
    maxDate: expiration ? earliestOf([agentLatest, expiration]) : agentLatest
  };
};
